import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  BrainCircuit, 
  TrendingUp, 
  Target, 
  DollarSign, 
  Zap, 
  Calendar, 
  Filter, 
  Info,
  MousePointer2,
  MessageSquare,
  Clock,
  Sparkles,
  ShieldCheck,
  Repeat,
  Layers,
  RefreshCw,
  Inbox,
  Activity
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip 
} from 'recharts';
import './PurchasePredictor.css';
import { getApiUrl } from '../../config/api';


const API_URL = getApiUrl('/analytics/purchase-predictor');
const getToken = () => localStorage.getItem('access_token') || '';


// Maps a model-driver label to a display icon since the API only returns label/weight/color
const SIGNAL_ICON_MAP = {
  'message sentiment': MessageSquare,
  'click recency': MousePointer2,
  'platform timing': Clock,
  'offer interaction': Zap,
};

const getSignalIcon = (label = '') => {
  const key = label.toLowerCase();
  for (const k in SIGNAL_ICON_MAP) {
    if (key.includes(k)) return SIGNAL_ICON_MAP[k];
  }
  return Activity;
};


// Maps an impact-segment label to a display icon since the API only returns label/value/trend/color
const IMPACT_ICON_MAP = {
  'vip winback': ShieldCheck,
  'repeat rate': Repeat,
  'ltv': Layers,
  'subscription': DollarSign,
};

const getImpactIcon = (label = '') => {
  const key = label.toLowerCase();
  for (const k in IMPACT_ICON_MAP) {
    if (key.includes(k)) return IMPACT_ICON_MAP[k];
  }
  return Target;
};


// Extracts a numeric percent from strings like "84% Odds" or "24.8%" for the progress bar
const extractPercent = (value) => {
  if (typeof value !== 'string') return undefined;
  const match = value.match(/(\d+(\.\d+)?)\s*%/);
  return match ? Math.min(Math.max(Number(match[1]), 0), 100) : undefined;
};


const PurchasePredictor = () => {
  const [activeIntentFilter, setActiveIntentFilter] = useState(null);
  const [predictorData, setPredictorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchPredictorData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(API_URL, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const json = await res.json();
      setPredictorData(json);
    } catch (err) {
      console.error('Purchase Predictor fetch error:', err);
      setError('Unable to load live prediction data from the server.');
      setPredictorData(null);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchPredictorData();
  }, [fetchPredictorData]);


  const intent = predictorData?.intent_classification || {};
  const highCount = intent.high?.count ?? 0;
  const mediumCount = intent.medium?.count ?? 0;
  const lowCount = intent.low?.count ?? 0;
  const highRev = intent.high?.est_rev ?? '$0k';
  const mediumRev = intent.medium?.est_rev ?? '$0k';
  const lowRev = intent.low?.est_rev ?? '$0k';


  // Data for Intent Distribution Donut — sourced from live API
  const intentData = [
    { name: 'High Intent', value: highCount, estRev: highRev, color: '#F97316', glow: 'rgba(249, 115, 22, 0.4)' },
    { name: 'Medium Intent', value: mediumCount, estRev: mediumRev, color: '#3B82F6', glow: 'rgba(59, 130, 246, 0.4)' },
    { name: 'Low Intent', value: lowCount, estRev: lowRev, color: '#64748B', glow: 'rgba(100, 116, 139, 0.4)' },
  ];


  const totalProfiles = intentData.reduce((sum, item) => sum + item.value, 0);
  const hasIntentData = totalProfiles > 0;


  const revenueGrowth = predictorData?.revenue_growth ?? 0;


  // Forecast chart now reads forecast_data (with confidence bands) directly from the API
  const forecastData = Array.isArray(predictorData?.forecast_data) ? predictorData.forecast_data : [];
  const hasForecastData = forecastData.length > 0;


  // Model Drivers now come entirely from the API's signals array
  const signals = (Array.isArray(predictorData?.signals) ? predictorData.signals : []).map((signal, idx) => ({
    label: signal.label || `Signal ${idx + 1}`,
    weight: signal.weight ?? 0,
    color: signal.color || 'blue',
    icon: getSignalIcon(signal.label || ''),
  }));
  const hasSignals = signals.length > 0;


  // Individual Forecasts table now comes entirely from the API's customers array
  const customers = Array.isArray(predictorData?.customers) ? predictorData.customers : [];
  const hasCustomers = customers.length > 0;


  // High-Impact Segments now come entirely from the API's impact_segments array
  const impactSegments = (Array.isArray(predictorData?.impact_segments) ? predictorData.impact_segments : []).map((segment, idx) => ({
    label: segment.label || `Segment ${idx + 1}`,
    subtitle: segment.subtitle || '',
    value: segment.value ?? '—',
    trend: segment.trend,
    color: segment.color || 'blue',
    icon: getImpactIcon(segment.label || ''),
    progress: segment.progress ?? extractPercent(segment.value),
  }));
  const hasImpactSegments = impactSegments.length > 0;


  return (
    <>
      <Helmet>
        <title>Purchase Predictor - Revexia AI Revenue Brain</title>
        <meta name="description" content="Forecast customer intent and revenue with AI" />
      </Helmet>


      <div className="predictor-page-wrapper">
        <div className="container-fluid px-4">
          {/* View Toolbar */}
          <header className="predictor-toolbar">
            <p className="toolbar-description">
              Forecast customer intent, revenue, and future value with 98.2% accuracy
            </p>
            <div className="toolbar-actions">
              {error && (
                <div className="fallback-notice">
                  <Inbox size={14} />
                  <span>{error}</span>
                </div>
              )}
              <button className="toolbar-btn" onClick={fetchPredictorData} disabled={loading}>
                <RefreshCw size={16} className={loading ? 'spin' : ''} />
                <span>{loading ? 'Refreshing' : 'Refresh Data'}</span>
              </button>
              <button className="toolbar-btn">
                <Calendar size={16} />
                <span>60 Days Forecast</span>
              </button>
              <button className="toolbar-icon-btn">
                <Filter size={20} />
              </button>
            </div>
          </header>


          {/* Top Prediction Analytics */}
          <div className="row g-4 mb-5">
            {/* Intent Distribution Donut */}
            <div className="col-12 col-lg-4">
              <div className="intent-distribution-card">
                <div className="intent-card-header">
                  <h3 className="intent-card-title">Intent Spread</h3>
                  <Info size={16} className="intent-info-icon" />
                </div>


                {loading ? (
                  <div className="predictor-loading-block">
                    <div className="loading-spinner"></div>
                    <p>Loading intent data...</p>
                  </div>
                ) : !hasIntentData ? (
                  <div className="predictor-empty-block">
                    <Inbox size={26} />
                    <p>No intent data available yet</p>
                  </div>
                ) : (
                  <>
                    <div className="intent-chart-wrapper">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={intentData}
                            innerRadius={80}
                            outerRadius={100}
                            paddingAngle={8}
                            dataKey="value"
                            stroke="none"
                            onClick={(data) => setActiveIntentFilter(data.name)}
                          >
                            {intentData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.color}
                                className="intent-pie-cell"
                                style={{ filter: `drop-shadow(0 0 8px ${entry.glow})` }}
                              />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#0D0F29', 
                              border: '1px solid rgba(255,255,255,0.1)', 
                              borderRadius: '12px' 
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="intent-center-label">
                        <span className="intent-total-value">{(totalProfiles / 1000).toFixed(1)}K</span>
                        <span className="intent-total-label">Profiles</span>
                      </div>
                    </div>


                    <div className="intent-legend-list">
                      {intentData.map((item) => (
                        <div 
                          key={item.name}
                          className={`intent-legend-item ${activeIntentFilter === item.name ? 'active' : ''}`}
                          onClick={() => setActiveIntentFilter(item.name)}
                        >
                          <div className="intent-legend-left">
                            <div 
                              className="intent-legend-dot"
                              style={{ 
                                backgroundColor: item.color, 
                                boxShadow: `0 0 10px ${item.glow}` 
                              }}
                            ></div>
                            <span className="intent-legend-name">{item.name}</span>
                          </div>
                          <div className="intent-legend-right">
                            <span className="intent-legend-percent">
                              {totalProfiles > 0 ? ((item.value / totalProfiles) * 100).toFixed(1) : '0.0'}%
                            </span>
                            <span className="intent-legend-rev">{item.estRev}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>


            {/* Predictive Revenue Forecast */}
            <div className="col-12 col-lg-8">
              <div className="revenue-forecast-card">
                <div className="forecast-card-header">
                  <div className="forecast-header-left">
                    <h3 className="forecast-card-title">30-Day Revenue Projection</h3>
                    <p className="forecast-card-subtitle">
                      AI modeling based on current engagement velocity
                    </p>
                  </div>
                  <div className={`forecast-growth-badge ${revenueGrowth < 0 ? 'negative' : ''}`}>
                    <TrendingUp size={16} />
                    <span>{revenueGrowth >= 0 ? '+' : ''}{revenueGrowth}% Forecasted Growth</span>
                  </div>
                </div>


                {loading ? (
                  <div className="predictor-loading-block">
                    <div className="loading-spinner"></div>
                    <p>Loading forecast...</p>
                  </div>
                ) : !hasForecastData ? (
                  <div className="predictor-empty-block">
                    <Inbox size={26} />
                    <p>No revenue forecast available yet</p>
                  </div>
                ) : (
                  <div className="forecast-chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={forecastData}>
                        <defs>
                          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid 
                          strokeDasharray="3 3" 
                          vertical={false} 
                          stroke="rgba(255,255,255,0.05)" 
                        />
                        <XAxis 
                          dataKey="name" 
                          axisLine={false} 
                          tickLine={false} 
                          tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} 
                          dy={10} 
                        />
                        <YAxis 
                          axisLine={false} 
                          tickLine={false} 
                          tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} 
                          tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`} 
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(13, 15, 41, 0.95)', 
                            border: '1px solid rgba(255,255,255,0.1)', 
                            borderRadius: '12px', 
                            backdropFilter: 'blur(4px)' 
                          }}
                          itemStyle={{ color: '#fff' }}
                          formatter={(value, name, props) => {
                            const conf = props?.payload?.confidence;
                            if (Array.isArray(conf) && conf.length === 2) {
                              return [`$${value.toLocaleString()} (range: $${conf[0].toLocaleString()}–$${conf[1].toLocaleString()})`, 'Projected'];
                            }
                            return [`$${value.toLocaleString()}`, 'Projected'];
                          }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#3B82F6" 
                          strokeWidth={4} 
                          fillOpacity={1} 
                          fill="url(#colorValue)" 
                          animationDuration={2000}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* High-Impact Segments */}
          <section className="impact-segments-section">
            <div className="section-header-with-icon">
              <Target size={20} className="section-icon orange" />
              <h3 className="section-main-title">High-Impact Segments</h3>
            </div>
            {loading ? (
              <div className="predictor-loading-block">
                <div className="loading-spinner"></div>
                <p>Loading segments...</p>
              </div>
            ) : !hasImpactSegments ? (
              <div className="predictor-empty-block">
                <Inbox size={26} />
                <p>No high-impact segments available yet</p>
              </div>
            ) : (
              <div className="row g-4">
                {impactSegments.map((segment, index) => (
                  <div key={index} className="col-12 col-sm-6 col-lg-3">
                    <div className={`impact-segment-card segment-${segment.color}`}>
                      <div className="impact-card-header">
                        <div className="impact-card-icon">
                          <segment.icon size={20} />
                        </div>
                        <div className="impact-card-info">
                          <p className="impact-card-label">{segment.label}</p>
                          {segment.subtitle && (
                            <p className="impact-card-subtitle">{segment.subtitle}</p>
                          )}
                        </div>
                      </div>
                      <div className="impact-card-body">
                        <h4 className="impact-card-value">{segment.value}</h4>
                        {segment.trend !== undefined && segment.trend !== 'Flat' && (
                          <span className={`impact-card-trend ${segment.trend < 0 ? 'negative' : 'positive'}`}>
                            {segment.trend >= 0 ? '+' : ''}{segment.trend}%
                          </span>
                        )}
                        {segment.trend === 'Flat' && (
                          <span className="impact-card-trend neutral">Flat</span>
                        )}
                      </div>
                      {segment.progress !== undefined && (
                        <div className="impact-progress-track">
                          <div 
                            className={`impact-progress-bar progress-${segment.color}`}
                            style={{ width: `${segment.progress}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>


          {/* Customer Forecasts & Model Drivers */}
          <div className="row g-4 mb-5">
            {/* Individual Forecasts Table */}
            <div className="col-12 col-lg-8">
              <div className="forecasts-table-card">
                <div className="forecasts-table-header">
                  <div className="forecasts-header-left">
                    <h3 className="forecasts-table-title">Individual Forecasts</h3>
                    <p className="forecasts-table-subtitle">
                      Top customers ranked by predictive probability
                    </p>
                  </div>
                  <button className="view-all-btn">View All Profiles</button>
                </div>


                {loading ? (
                  <div className="predictor-loading-block">
                    <div className="loading-spinner"></div>
                    <p>Loading customer forecasts...</p>
                  </div>
                ) : !hasCustomers ? (
                  <div className="predictor-empty-block">
                    <Inbox size={26} />
                    <p>No customer forecasts available yet</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="forecasts-table">
                      <thead>
                        <tr>
                          <th>Customer / Current Intent</th>
                          <th>Predicted Window</th>
                          <th>Exp. Lifetime Value</th>
                          <th className="text-end">Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customers.map((customer, index) => (
                          <tr key={index} className="forecast-row">
                            <td>
                              <div className="customer-cell">
                                <div className="customer-avatar">
                                  <BrainCircuit size={18} />
                                </div>
                                <div className="customer-info">
                                  <p className="customer-name">{customer.name}</p>
                                  <p className={`customer-intent ${(customer.intent || '').toLowerCase()}`}>
                                    {customer.intent} Intent
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="window-cell">{customer.window}</td>
                            <td className="ltv-cell">{customer.ltv}</td>
                            <td className="confidence-cell">
                              <div className="confidence-wrapper">
                                <span className="confidence-score">{customer.score}%</span>
                                <div className="confidence-bar-track">
                                  <div 
                                    className="confidence-bar-fill"
                                    style={{ width: `${customer.score}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>


            {/* Model Drivers */}
            <div className="col-12 col-lg-4">
              <div className="model-drivers-card">
                <div className="drivers-card-header">
                  <div className="drivers-icon-wrapper">
                    <Sparkles size={20} />
                  </div>
                  <h3 className="drivers-card-title">Model Drivers</h3>
                </div>
                <p className="drivers-card-description">
                  Real-time weights assigned to behavioral signals influencing the prediction engine.
                </p>


                {loading ? (
                  <div className="predictor-loading-block">
                    <div className="loading-spinner"></div>
                    <p>Loading signals...</p>
                  </div>
                ) : !hasSignals ? (
                  <div className="predictor-empty-block">
                    <Inbox size={20} />
                    <p>No model signals available yet</p>
                  </div>
                ) : (
                  <div className="drivers-list">
                    {signals.map((signal, index) => (
                      <div key={index} className="driver-item">
                        <div className="driver-item-header">
                          <div className="driver-item-left">
                            <div className="driver-icon">
                              <signal.icon size={14} />
                            </div>
                            <span className="driver-label">{signal.label}</span>
                          </div>
                          <span className="driver-weight">{signal.weight}%</span>
                        </div>
                        <div className="driver-progress-track">
                          <div 
                            className={`driver-progress-bar progress-${signal.color}`}
                            style={{ width: `${signal.weight}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}


                <div className="explainable-ai-box">
                  <p className="explainable-ai-label">Explainable AI Node</p>
                  <p className="explainable-ai-text">
                    "{predictorData?.explainable_ai || 'Conversation quality and message engagement are currently the primary drivers for purchase predictions.'}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


export default PurchasePredictor;